import api from './api';
import { saveAs } from 'file-saver';

const state = {
	adapters: [],
	loading: false,
	error: false,
	message: null,
	success: false,
	latestAdapterId: null,
	limit: 10,
	page: 0,
	foundAll: false,
	adapterRuns: [],
	uploadedFile: null,
};
const mutations = {
	startLoading(state) {
		state.loading = true;
	},
	stopLoading(state) {
		state.loading = false;
	},
	setAdapters(state, { adapters }) {
		state.adapters = adapters;
	},
	setAdapterRuns(state, { adapterRuns }) {
		state.adapterRuns = adapterRuns;
	},
	appendAdapterRuns(state, { adapterRuns }) {
		state.adapterRuns = state.adapterRuns.concat(adapterRuns);
	},
	setError(state, { message }) {
		state.error = true;
		state.message = message;
	},
	removeError(state) {
		state.error = false;
		state.message = null;
	},
	showSuccess(state, { message }) {
		state.success = true;
		state.message = message;
	},
	hideSuccess(state) {
		state.success = false;
		state.message = null;
	},
	setLatestAdapterId(state, { id }) {
		state.latestAdapterId = id;
	},
	reset(state) {
		state.page = 0;
		state.adapterRuns = [];
		state.foundAll = false;
	},
	updateOffset(state) {
		state.page += 1;
	},
	foundAll(state) {
		state.foundAll += true;
	},
	reducePage(state) {
		state.page -= 1;
	},
	updateAdapterRun(state, { adapterRun }) {
		let adapterRunsClone = [...state.adapterRuns];
		const idx = adapterRunsClone.findIndex(run => run.id == adapterRun.id);
		adapterRun.created = new Date(adapterRun.createdAt).toISOString().substring(0, 10);
		adapterRun.started = new Date(adapterRun.startedAt).toISOString().substring(11, 19);
		adapterRun.duration = new Date(adapterRun.endedAt) - new Date(adapterRun.startedAt);
		adapterRunsClone[idx] = adapterRun;
		state.adapterRuns = adapterRunsClone;
	},
	setUploadedAdapter(state, file) {
		state.uploadedFile = file;
	},
};

const getters = {};

const actions = {
	getAdapters({ state, commit }) {
		commit('startLoading');
		return api
			.getAdapters(true)
			.then(response => {
				const adapters = response.data;
				commit('setAdapters', { adapters });
				commit('stopLoading');
			})
			.catch(err => {
				const message = err.response.data.message;
				commit('setError', { message });
				commit('stopLoading');
				window.setTimeout(() => {
					commit('removeError');
				}, 300);
			});
	},
	deleteAdapter({ state, commit }, adapter) {
		commit('startLoading');
		var result;
		if (adapter.deleted) {
			let adapterClone = { ...adapter };
			adapterClone.deleted = false;
			result = api.editAdapter(adapterClone);
		} else {
			result = api.deleteAdapter(adapter);
		}
		return result
			.then(response => {
				const message = 'Adapter deleted successfully';
				commit('stopLoading');
				commit('showSuccess', { message });
				window.setTimeout(() => {
					commit('hideSuccess');
				}, 1500);
			})
			.catch(err => {
				const message = err.response.data.message;
				commit('setError', { message });
				commit('stopLoading');
				window.setTimeout(() => {
					commit('removeError');
				}, 3000);
			});
	},
	undeleteAdapter({ state, commit }, adapter) {
		return api.undeleteAdapter(adapter);
	},
	editAdapter({ state, commit }, adapter) {
		commit('startLoading');
		commit('setLatestAdapterId', { id: adapter.id });

		return api
			.editAdapter(adapter)
			.then(response => {
				const message = 'Changes saved successfully';
				commit('stopLoading');
				commit('showSuccess', { message });
				window.setTimeout(() => {
					commit('hideSuccess');
				}, 1500);
			})
			.catch(err => {
				const message = err.response.data.message;
				commit('setError', { message });
				commit('stopLoading');
				window.setTimeout(() => {
					commit('removeError');
				}, 3000);
			});
	},
	createAdapter({ state, commit }, adapter) {
		commit('startLoading');
		return api
			.createAdapter(adapter)
			.then(response => {
				const message = 'Adapter added successfully';
				commit('stopLoading');
				commit('showSuccess', { message });
				window.setTimeout(() => {
					commit('hideSuccess');
				}, 1500);
			})
			.catch(err => {
				const message = err.response.data.message;
				commit('setError', { message });
				commit('stopLoading');
				window.setTimeout(() => {
					commit('removeError');
				}, 3000);
			});
	},
	loadAdapter({ state, commit }, id) {
		return api
			.loadAdapter(id)
			.then(response => {
				const adapter = response.data;
				return adapter;
			})
			.catch(err => {
				const message = err.response.data.message;
				commit('setError', { message });
				commit('stopLoading');
				window.setTimeout(() => {
					commit('removeError');
				}, 1500);
			});
	},
	async downloadAdapter({ dispatch }, id) {
		const adapter = await dispatch('loadAdapter', id);
		const blob = new Blob([JSON.stringify(adapter, null, 4)], { type: 'text/plain;charset=utf-8' });
		saveAs(blob, `${adapter.class}_${adapter.id}.json`);
	},
	async uploadAdapter({ state, commit, dispatch }, file) {
		commit('startLoading');
		commit('setUploadedAdapter', file);
		const fileData = await new Promise(function(resolve, reject) {
			var reader = new FileReader();
			reader.onload = function() {
				resolve(reader.result);
			};
			reader.onerror = reject;
			reader.readAsText(file);
		});
		const adapter = JSON.parse(fileData);
		commit('setUploadedAdapter', null);

		try {
			await api.createAdapter(adapter);
			const message = 'Adapter imported successfully';
			dispatch('getAdapters');
			commit('stopLoading');
			commit('showSuccess', { message });
			window.setTimeout(() => {
				commit('hideSuccess');
			}, 1500);
		} catch (err) {
			const message = err.response.data.message;
			commit('setError', { message });
			commit('stopLoading');
			window.setTimeout(() => {
				commit('removeError');
			}, 3000);
		}
	},
	async importArchive({ state, commit, dispatch }, { id, formData }) {
		commit('startLoading');

		try {
			await api.importArchive(id, formData);
			const message = 'Uploaded VDV data successfully';
			commit('stopLoading');
			commit('showSuccess', { message });
			window.setTimeout(() => {
				commit('hideSuccess');
			}, 1500);
		} catch (err) {
			const message = err.response.data.message;
			commit('setError', { message });
			commit('stopLoading');
			window.setTimeout(() => {
				commit('removeError');
			}, 3000);
		}
	},
	runAdapter({ state, commit }, { id, reset, fix }) {
		commit('startLoading');
		commit('setLatestAdapterId', { id });
		return api
			.runAdapter(id, reset, fix)
			.then(() => {
				let message;
				if (reset) {
					message = 'Adapter reset successfull!';
				} else {
					message = 'Manual adapter run successfull!';
				}
				commit('stopLoading');
				commit('showSuccess', { message });
				window.setTimeout(() => {
					commit('hideSuccess');
				}, 1500);
			})
			.catch(err => {
				const message = err.response.data.message;
				commit('stopLoading');
				commit('setError', { message });
				window.setTimeout(() => {
					commit('removeError');
				}, 3000);
			});
	},
	getLastAdapterRun({ state, commit }, { id, limit, offset }) {
		commit('startLoading');
		return api
			.getAdapterRuns(id, limit, offset)
			.then(response => {
				const adapterRuns = response.data;
				commit('stopLoading');
				if (!adapterRuns) {
					return null;
				}
				return adapterRuns[0];
			})
			.catch(err => {
				const message = err.response.data.message;
				commit('stopLoading');
				commit('setError', { message });
				window.setTimeout(() => {
					commit('removeError');
				}, 3000);
			});
	},
	getAdapterRuns({ state, commit }, { id, limit, offset }) {
		commit('startLoading');
		commit('updateOffset');
		return api
			.getAdapterRuns(id, limit, offset)
			.then(response => {
				const adapterRuns = response.data;
				if (offset == 0) {
					commit('setAdapterRuns', { adapterRuns });
				} else {
					commit('appendAdapterRuns', { adapterRuns });
				}
				commit('stopLoading');
				if (!adapterRuns) {
					return null;
				}
				return adapterRuns;
			})
			.catch(err => {
				const message = err.response.data.message;
				commit('stopLoading');
				commit('setError', { message });
				window.setTimeout(() => {
					commit('removeError');
				}, 3000);
			});
	},
	socket_frameworkReloadAdapterRuns({ dispatch, state, commit }, { id }) {
		const limit = state.limit * state.page;
		commit('reducePage');
		dispatch('getAdapterRuns', { id, limit: limit, offset: 0 });
	},
	socket_frameworkUpdateAdapterRun({ dispatch, state, commit }, { id }) {
		return api
			.findAdapterRun(id)
			.then(response => {
				const adapterRun = response.data;
				commit('updateAdapterRun', { adapterRun });
			})
			.catch(err => {
				const message = err.response.data.message;
				commit('showError', { message });
				window.setTimeout(() => {
					commit('hideError');
				}, 3000);
				return message;
			});
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
